import { api } from "@slapinc/slappos-backend/api";
import { createFileRoute } from "@tanstack/react-router";
import { useMutation, usePaginatedQuery, useQuery } from "convex/react";
import type { GenericId as Id } from "convex/values";
import { LayoutPanelTop } from "lucide-react";
import { useCallback, useMemo, useState } from "react";
import { Can } from "@/components/can";
import { PageContent } from "@/components/layout/page-content";
import { CreatePromotionModal } from "@/components/modals/create-promotion-modal";
import { EditPromotionModal } from "@/components/modals/edit-promotion-modal";
import { ActionHeaderButton } from "@/components/ui/action-header-button";
import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { DisplaySelect } from "@/components/ui/display-select";
import {
	Empty,
	EmptyDescription,
	EmptyHeader,
	EmptyMedia,
	EmptyTitle,
} from "@/components/ui/empty";
import { SearchInput } from "@/components/ui/updated-search-input";
import { PromotionsTable } from "@/features/promotions/_components/promotions-table";
import { useLocalStorageSettings } from "@/hooks/use-local-storage-settings";
import { useMe } from "@/hooks/use-me";
import { usePagination } from "@/hooks/use-pagination";
import { toSatangs } from "@/lib/currency";
import { handleConvexError } from "@/lib/errors";
import type { PromotionFormValues } from "@/lib/schemas/promotion";
import { toast } from "@/lib/toast";
import type { Promotion } from "@/lib/types/promotion";

interface DisplaySettings {
	viewMode: "cards" | "rows";
	showHeaders: boolean;
	sortKey: string;
	columnVisibility: Record<string, boolean>;
}

const DEFAULT_SETTINGS: DisplaySettings = {
	viewMode: "rows",
	showHeaders: true,
	sortKey: "name",
	columnVisibility: {
		name: true,
		type: true,
		discount: true,
		schedule: true,
		status: true,
	},
};

export const Route = createFileRoute("/(dashboard)/promotions")({
	component: PromotionsPage,
});

const PAGE_SIZE = 10;

function PromotionsPage() {
	// Get current user for mutations
	const me = useMe();
	const orgId = me?.activeOrganizationId;

	const {
		settings: displaySettings,
		hasChanges,
		handleReset,
		handleSetDefault,
		updateSetting,
		updateColumnVisibility,
	} = useLocalStorageSettings<DisplaySettings>(
		"slappos_promotions_display_settings",
		DEFAULT_SETTINGS,
		() => {
			const defaultMode =
				globalThis.window !== undefined && globalThis.innerWidth < 640
					? "cards"
					: "rows";
			return { ...DEFAULT_SETTINGS, viewMode: defaultMode };
		},
	);

	// State
	const [searchQuery, setSearchQuery] = useState("");
	const [showActiveOnly, setShowActiveOnly] = useState(false);
	const { pagination, onPaginationChange, resetPage } =
		usePagination(PAGE_SIZE);

	// Modal state
	const [createModalOpen, setCreateModalOpen] = useState(false);
	const [editModalOpen, setEditModalOpen] = useState(false);
	const [selectedPromotion, setSelectedPromotion] = useState<Promotion | null>(
		null,
	);
	const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
	const [promotionToDelete, setPromotionToDelete] = useState<Promotion | null>(
		null,
	);

	// Mutations
	const createPromotion = useMutation(api.features.promotions.web.create);
	const updatePromotion = useMutation(api.features.promotions.web.update);
	const deletePromotion = useMutation(api.features.promotions.web.remove);

	// Loading states
	const [isCreating, setIsCreating] = useState(false);
	const [isUpdating, setIsUpdating] = useState(false);
	const [isDeleting, setIsDeleting] = useState(false);

	// Queries
	const promotionsQuery = useQuery(api.features.promotions.web.list, {
		activeOnly: showActiveOnly,
	});
	const productsQuery = usePaginatedQuery(
		api.features.products.web.list,
		{},
		{ initialNumItems: 100 },
	);
	const categoriesQuery = usePaginatedQuery(
		api.features.categories.web.list,
		{},
		{ initialNumItems: 50 },
	);
	const branchesQuery = useQuery(api.features.branches.web.list, {});

	const allPromotions = (promotionsQuery ?? []) as Promotion[];
	const products = productsQuery.results ?? [];
	const categories = categoriesQuery.results ?? [];
	const branches = branchesQuery ?? [];
	const isLoading = promotionsQuery === undefined;

	// Filter promotions based on search
	const filteredPromotions = useMemo(() => {
		let result = allPromotions;

		if (searchQuery) {
			const query = searchQuery.toLowerCase();
			result = result.filter((p) => p.name.toLowerCase().includes(query));
		}

		// Sort based on settings
		return result.sort((a, b) => {
			if (displaySettings.sortKey === "name") {
				return a.name.localeCompare(b.name);
			}
			if (displaySettings.sortKey === "date") {
				return (b._creationTime || 0) - (a._creationTime || 0);
			}
			if (displaySettings.sortKey === "status") {
				return Number(b.isActive) - Number(a.isActive);
			}
			return 0;
		});
	}, [allPromotions, searchQuery, displaySettings.sortKey]);

	// Paginate
	const paginatedPromotions = useMemo(() => {
		const start = pagination.pageIndex * pagination.pageSize;
		const end = start + pagination.pageSize;
		return filteredPromotions.slice(start, end);
	}, [filteredPromotions, pagination.pageIndex, pagination.pageSize]);

	// Handlers
	const handleSearchChange = useCallback(
		(value: string) => {
			setSearchQuery(value);
			resetPage();
		},
		[resetPage],
	);

	const handleToggleActiveOnly = useCallback(() => {
		setShowActiveOnly((prev) => !prev);
		resetPage();
	}, [resetPage]);

	const handleEdit = useCallback((promotion: Promotion) => {
		setSelectedPromotion(promotion);
		setEditModalOpen(true);
	}, []);

	const handleDuplicate = useCallback(
		async (promotion: Promotion) => {
			if (!orgId) return;

			setIsCreating(true);
			try {
				await createPromotion({
					name: `${promotion.name} (Copy)`,
					trigger: promotion.trigger,
					triggerThreshold: promotion.triggerThreshold,
					discountType: promotion.discountType,
					discountValueSatangs: promotion.discountValueSatangs,
					discountPercent:
						promotion.discountBasisPoints === undefined
							? undefined
							: promotion.discountBasisPoints / 100,
					freeQuantity: promotion.freeQuantity,
					unlockedPriceSatangs: promotion.unlockedPriceSatangs,
					productIds: promotion.productIds,
					categoryIds: promotion.categoryIds,
					branchIds: promotion.branchIds,
					startsAt: promotion.startsAt,
					endsAt: promotion.endsAt,
					isActive: false, // Duplicates start inactive
				});
				toast.success(<>Promotion duplicated</>);
			} catch (error) {
				handleConvexError(error, "Failed to duplicate promotion");
			} finally {
				setIsCreating(false);
			}
		},
		[orgId, createPromotion],
	);

	const handleDelete = useCallback((promotion: Promotion) => {
		setPromotionToDelete(promotion);
		setDeleteDialogOpen(true);
	}, []);

	const confirmDelete = useCallback(async () => {
		if (!promotionToDelete) return;

		setIsDeleting(true);
		try {
			await deletePromotion({ promotionId: promotionToDelete._id });
			toast.success(<>Promotion deleted</>);
			setDeleteDialogOpen(false);
			setPromotionToDelete(null);
		} catch (error) {
			handleConvexError(error, "Failed to delete promotion");
		} finally {
			setIsDeleting(false);
		}
	}, [promotionToDelete, deletePromotion]);

	const handleCreatePromotion = useCallback(
		async (data: PromotionFormValues) => {
			if (!orgId) return;

			setIsCreating(true);
			try {
				// Convert form discount value to appropriate backend field
				let discountValueSatangs: number | undefined;
				let discountPercent: number | undefined;
				if (
					data.discountType === "amount" &&
					typeof data.discountValue === "number"
				) {
					discountValueSatangs = toSatangs(data.discountValue);
				} else if (
					data.discountType === "percentage" &&
					typeof data.discountValue === "number"
				) {
					discountPercent = data.discountValue;
				}

				await createPromotion({
					name: data.name,
					trigger: data.trigger as Promotion["trigger"],
					triggerThreshold:
						typeof data.triggerThreshold === "number"
							? data.triggerThreshold
							: undefined,
					discountType: data.discountType ? data.discountType : undefined,
					discountValueSatangs: discountValueSatangs ?? undefined,
					discountPercent: discountPercent ?? undefined,
					freeQuantity:
						typeof data.freeQuantity === "number"
							? data.freeQuantity
							: undefined,
					unlockedPriceSatangs:
						typeof data.unlockedPrice === "number"
							? toSatangs(data.unlockedPrice)
							: undefined,
					productIds:
						data.promotionType === "product" && data.products.length > 0
							? (data.products as Id<"products">[])
							: undefined,
					categoryIds:
						data.promotionType === "category" && data.categories.length > 0
							? (data.categories as Id<"categories">[])
							: undefined,
					branchIds:
						data.branches.length > 0
							? (data.branches as Id<"branches">[])
							: undefined,
					startsAt: data.dateRange[0]?.getTime(),
					endsAt: data.dateRange[1]?.getTime(),
					isActive: data.isActive,
				});
				toast.success(<>Promotion created</>);
			} catch (error) {
				handleConvexError(error, "Failed to create promotion");
				throw error;
			} finally {
				setIsCreating(false);
			}
		},
		[orgId, createPromotion],
	);

	const handleUpdatePromotion = useCallback(
		async (data: PromotionFormValues) => {
			if (!orgId || !selectedPromotion) return;

			setIsUpdating(true);
			try {
				// Convert form discount value to appropriate backend field
				let discountValueSatangs: number | null = null;
				let discountPercent: number | null = null;
				if (
					data.discountType === "amount" &&
					typeof data.discountValue === "number"
				) {
					discountValueSatangs = toSatangs(data.discountValue);
				} else if (
					data.discountType === "percentage" &&
					typeof data.discountValue === "number"
				) {
					discountPercent = data.discountValue;
				}

				await updatePromotion({
					promotionId: selectedPromotion._id,
					name: data.name,
					trigger: data.trigger as Promotion["trigger"],
					triggerThreshold:
						typeof data.triggerThreshold === "number"
							? data.triggerThreshold
							: undefined,
					discountType: data.discountType ? data.discountType : undefined,
					discountValueSatangs: discountValueSatangs ?? undefined,
					discountPercent: discountPercent ?? undefined,
					freeQuantity:
						typeof data.freeQuantity === "number"
							? data.freeQuantity
							: undefined,
					unlockedPriceSatangs:
						typeof data.unlockedPrice === "number"
							? toSatangs(data.unlockedPrice)
							: undefined,
					productIds:
						data.promotionType === "product" && data.products.length > 0
							? (data.products as Id<"products">[])
							: undefined,
					categoryIds:
						data.promotionType === "category" && data.categories.length > 0
							? (data.categories as Id<"categories">[])
							: undefined,
					branchIds:
						data.branches.length > 0
							? (data.branches as Id<"branches">[])
							: undefined,
					startsAt: data.dateRange[0]?.getTime() ?? undefined,
					endsAt: data.dateRange[1]?.getTime() ?? undefined,
					isActive: data.isActive,
				});
				toast.success(<>Promotion updated</>);
			} catch (error) {
				handleConvexError(error, "Failed to update promotion");
				throw error;
			} finally {
				setIsUpdating(false);
			}
		},
		[orgId, selectedPromotion, updatePromotion],
	);

	return (
		<PageContent
			title="Promotions"
			controls={
				<Can scope="promotions.create">
					<ActionHeaderButton
						label="Add Promotion"
						onClick={() => setCreateModalOpen(true)}
					/>
				</Can>
			}
		>
			<div className="flex flex-col gap-4 sm:gap-6">
				{/* Search and Filter */}
				<div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:gap-2">
					<div className="sm:max-w-sm">
						<SearchInput
							placeholder="Search promotions..."
							value={searchQuery}
							onChange={(e) => handleSearchChange(e.target.value)}
						/>
					</div>
					<div className="flex items-center gap-2">
						<Button
							variant={showActiveOnly ? "default" : "outline"}
							size="sm"
							onClick={handleToggleActiveOnly}
							className="px-5 py-3 h-auto"
						>
							{showActiveOnly ? "Active Only" : "All Promotions"}
						</Button>
						<DisplaySelect
							viewMode={displaySettings.viewMode}
							onViewModeChange={(mode) => updateSetting("viewMode", mode)}
							selectedSort={displaySettings.sortKey}
							onSortChange={(sort) => updateSetting("sortKey", sort)}
							sortOptions={[
								{ value: "name", label: "Name" },
								{ value: "date", label: "Date Created" },
								{ value: "status", label: "Status" },
							]}
							options={[
								{
									key: "showHeaders",
									label: "Show table headers",
									checked: displaySettings.showHeaders,
									icon: <LayoutPanelTop className="size-4" />,
								},
							]}
							onOptionsChange={(key, checked) => {
								if (key === "showHeaders")
									updateSetting("showHeaders", checked);
							}}
							properties={[
								{
									key: "name",
									label: "Name",
									visible: displaySettings.columnVisibility.name,
								},
								{
									key: "type",
									label: "Type",
									visible: displaySettings.columnVisibility.type,
								},
								{
									key: "discount",
									label: "Discount",
									visible: displaySettings.columnVisibility.discount,
								},
								{
									key: "schedule",
									label: "Schedule",
									visible: displaySettings.columnVisibility.schedule,
								},
								{
									key: "status",
									label: "Status",
									visible: displaySettings.columnVisibility.status,
								},
							]}
							onPropertiesChange={updateColumnVisibility}
							onReset={handleReset}
							onSetDefault={handleSetDefault}
							showFooter={hasChanges}
						/>
					</div>
				</div>

				{/* Promotions Table */}
				{!isLoading && filteredPromotions.length === 0 ? (
					<Empty>
						<EmptyHeader>
							<EmptyMedia variant="icon">
								<LayoutPanelTop className="h-5 w-5 text-neutral-700" />
							</EmptyMedia>
							<EmptyTitle>No promotions</EmptyTitle>
							<EmptyDescription>
								{searchQuery
									? "No promotions match your search."
									: "Create a promotion to get started."}
							</EmptyDescription>
						</EmptyHeader>
					</Empty>
				) : (
					<PromotionsTable
						data={paginatedPromotions}
						loading={isLoading}
						pagination={pagination}
						onPaginationChange={onPaginationChange}
						rowCount={filteredPromotions.length}
						onEdit={handleEdit}
						onDuplicate={handleDuplicate}
						onDelete={handleDelete}
						viewMode={displaySettings.viewMode}
						showHeaders={displaySettings.showHeaders}
						columnVisibility={displaySettings.columnVisibility}
					/>
				)}
			</div>

			{/* Create Modal */}
			<CreatePromotionModal
				open={createModalOpen}
				onOpenChange={setCreateModalOpen}
				products={products}
				categories={categories}
				branches={branches}
				onSubmit={handleCreatePromotion}
				isSubmitting={isCreating}
			/>

			{/* Edit Modal */}
			<EditPromotionModal
				open={editModalOpen}
				onOpenChange={setEditModalOpen}
				promotion={selectedPromotion}
				products={products}
				categories={categories}
				branches={branches}
				onSubmit={handleUpdatePromotion}
				isSubmitting={isUpdating}
			/>

			{/* Delete Confirmation Dialog */}
			<AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
				<AlertDialogContent>
					<AlertDialogHeader>
						<AlertDialogTitle>Delete promotion?</AlertDialogTitle>
						<AlertDialogDescription>
							Are you sure you want to delete "{promotionToDelete?.name}"? This
							action cannot be undone.
						</AlertDialogDescription>
					</AlertDialogHeader>
					<AlertDialogFooter>
						<AlertDialogCancel disabled={isDeleting}>Cancel</AlertDialogCancel>
						<AlertDialogAction
							variant="destructive"
							onClick={confirmDelete}
							disabled={isDeleting}
						>
							{isDeleting ? "Deleting..." : "Delete"}
						</AlertDialogAction>
					</AlertDialogFooter>
				</AlertDialogContent>
			</AlertDialog>
		</PageContent>
	);
}
